<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CirclePlus, SlidersHorizontal, Wrench } from 'lucide-vue-next'
import type { ChangeCategory, ChangelogRelease } from '@/data/changelog'

const props = defineProps<{ release: ChangelogRelease }>()
const { t } = useI18n()
const categories = [
  { key: 'added', icon: CirclePlus },
  { key: 'improved', icon: SlidersHorizontal },
  { key: 'fixed', icon: Wrench },
] as const satisfies readonly { key: ChangeCategory; icon: unknown }[]
const groups = computed(() => categories.map((category) => ({
  ...category,
  items: props.release.items.filter((item) => item.category === category.key),
})).filter((category) => category.items.length))
</script>

<template>
  <div class="release-notes">
    <p v-if="release.summaryKey" class="release-summary">{{ t(release.summaryKey) }}</p>
    <section v-for="group in groups" :key="group.key" class="change-group" :data-category="group.key">
      <h3><span class="category-icon" aria-hidden="true"><component :is="group.icon" /></span>{{ t(`changelog.categories.${group.key}`) }}</h3>
      <ul class="change-list">
        <li v-for="item in group.items" :key="item.key">
          <strong>{{ t(`changelog.releases.${release.id}.items.${item.key}.title`) }}</strong>
          <span>{{ t(`changelog.releases.${release.id}.items.${item.key}.description`) }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.release-notes { font-size: var(--release-notes-font-size, 0.875rem); line-height: 1.75; overflow-wrap: anywhere; }
.release-summary { margin: 0; color: var(--muted-strong); }
.change-group { --category-accent: var(--muted-strong); margin-top: 1.5rem; }
.change-group[data-category='added'] { --category-accent: var(--green); }
.change-group[data-category='improved'] { --category-accent: var(--blue); }
.change-group[data-category='fixed'] { --category-accent: var(--amber); }
.change-group h3 { margin: 0 0 0.625rem; display: flex; align-items: center; gap: 0.5rem; color: var(--text-strong); font-size: var(--release-group-font-size, 0.8125rem); font-weight: 650; }
.category-icon { display: grid; place-items: center; flex: none; width: 1.5rem; height: 1.5rem; border-radius: 0.375rem; color: var(--category-accent); background: color-mix(in srgb, var(--category-accent) 10%, transparent); }
.category-icon svg { width: 0.9375rem; height: 0.9375rem; }
.change-list { margin: 0; padding-left: 1.125rem; display: grid; gap: 0.625rem; color: var(--text); list-style: disc; }
.change-list li { padding-left: 0.25rem; }
.change-list li::marker { color: color-mix(in srgb, var(--category-accent) 50%, var(--border)); font-size: 1em; }
.change-list strong { margin-right: 0.5rem; color: var(--text-strong); font-weight: 600; }
</style>
